import { DevicesComplianceCounts, Groups, LoadingState } from "../../../types";
import { useSelector } from "react-redux";
import { selectChildPractices, selectGroups, selectLoadingState } from "../../../redux/customer";
import { ChangeEvent, useMemo, useState } from "react";
import { Autocomplete, Card, Checkbox, FormControl, IconButton, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField, useTheme } from "@mui/material";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ComplianceStatus from "../../../components/ComplianceStatus";
import Stats from "../../../components/Stats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid";
import CompliancePlip from "../../../components/CompliancePlip";
import { getComplianceColor } from "../../../helpers/compliance";
import { useNavigate } from "react-router-dom";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type Props = {
  complianceCounts: DevicesComplianceCounts;
}

const ComplianceReport = ({ complianceCounts }: Props) => {
  const groups = useSelector(selectGroups);
  const childPractices = useSelector(selectChildPractices);
  
  const theme = useTheme();
  const navigate = useNavigate();

  const [complianceFilter, setComplianceFilter] = useState<number | undefined>(undefined);
  const [groupFilter, setGroupFilter] = useState<Groups[]>([]);
  const [search, setSearch] = useState<string>('');

  const loadingState = useSelector(selectLoadingState)

  const handleCheckboxComplianceFilter = (value: number) => {
    setComplianceFilter(prev => (prev === value ? undefined : value));
  }

  const handleComplianceFilter = (event: SelectChangeEvent<number | undefined>) => {
    const rawValue = event.target.value;
    const parsedValue = Number(rawValue);
    const value = isNaN(parsedValue) || parsedValue === 0 ? undefined : parsedValue;

    setComplianceFilter(prev => (prev === value ? undefined : value));
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
  };

  const filteredPractices = useMemo(() => {
    let practices = structuredClone(childPractices) ?? [];

    if (search.length > 0) {
      const cleanSearch = search.toLowerCase();
      practices = practices.filter(({ customer_number, name, street, city, state }) => {
        return customer_number.includes(cleanSearch)
          || name.toLowerCase().includes(cleanSearch)
          || street.toLowerCase().includes(cleanSearch)
          || city.toLowerCase().includes(cleanSearch)
          || state.toLowerCase().includes(cleanSearch)
      })
    }

    if (complianceFilter) {
      practices = practices.filter(practice =>
        complianceFilter === 4
          ? practice.device_status === 4 || practice.device_status == null
          : practice.device_status === complianceFilter
      )
    }

    if (groupFilter.length) {
      const members = groupFilter.flatMap((group) => group?.members.map((member) => member.customer_number));
      if (members) {
        practices = practices.filter((customer) => members.includes(customer.customer_number));
      }
    }

    return practices;
  }, [childPractices, complianceFilter, groupFilter, search])

  const statsContent = useMemo(() => {
    return [
      {
        description: 'Expired Items',
        value: complianceCounts.expiredCount,
        color: 'red'
      },
      {
        description: 'Expiring Items',
        value: complianceCounts.expiringCount,
        color: 'yellow'
      },
      {
        description: 'Backordered Items',
        value: complianceCounts.backorderedCount,
        color: 'yellow'
      },
      {
        description: 'Up to Date AEDs',
        value: complianceCounts.upToDateAeds,
      },
      {
        description: 'Up to Date Device Kits',
        value: complianceCounts.upToDateDeviceKits,
      }
    ]
  }, [complianceCounts])

  const isLoading = loadingState === LoadingState.Loading

  return (
    <>
      <div className="flex flex-space-between flex-align-start m-b-16">
        <ComplianceStatus complianceCounts={complianceCounts} complianceFilter={complianceFilter} handleComplianceFilter={handleCheckboxComplianceFilter} loading={isLoading} />
        <Stats loading={isLoading} statsHeader="Item" statsContent={statsContent} />
      </div>
      <Card variant="outlined">
        <div className="box-header b-b">{filteredPractices.length} Practices</div>
        <div className="flex flex-space-between m-16">
          <div className="flex">
            <FormControl sx={{ mr: 2, minWidth: 200 }} size="small">
              <InputLabel>Filter by Compliance</InputLabel>
              <Select value={complianceFilter ?? ''} label="Filter by Compliance" onChange={handleComplianceFilter}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={3}>Action Required</MenuItem>
                <MenuItem value={2}>Attention Needed</MenuItem>
                <MenuItem value={1}>Up to Date</MenuItem>
                <MenuItem value={4}>Service Inactive</MenuItem>
              </Select>
            </FormControl>
            {groups.length > 0 && (
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setGroupFilter(newValue);
                }}
                options={groups}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                size="small"
                style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
                renderInput={(params) => <TextField {...params} label="Filter by Group" />}
              />
            )}
          </div>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search"
            onChange={handleSearch}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                </IconButton>
              )
            }}
          />
        </div>
        <DataGrid
          sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
          rows={filteredPractices.map(practice => ({ ...practice, id: practice.customer_number }))}
          columns={[
            {
              field: "device_status",
              headerName: "Status",
              sortable: true,
              width: 100,
              renderCell: (params) => (
                <CompliancePlip message={params.row.device_message_short} status={params.row.device_status} />
              ),
              align: "center",
              getSortComparator: (sortDirection) => {
                const modifier = sortDirection === 'desc' ? -1 : 1;
                return (a, b, cellParams1, cellParams2) => {
                  if (a === b) {
                    return 0;
                  }

                  if (a === 4 || a === null) {
                    return 1;
                  }

                  if (b === 4 || b === null) {
                    return -1;
                  }

                  return (
                    modifier *
                    gridStringOrNumberComparator(a, b, cellParams1, cellParams2)
                  );
                };
              },
            },
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              minWidth: 500,
              renderCell: (params) => (
                <Link component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/devices`)} sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }}>
                  {params.row.name.trim()} ({params.row.customer_number})
                </Link>
              )
            },
            {
              field: "city",
              headerName: "Location",
              flex: 1,
              minWidth: 250,
              renderCell: (params) => (
                `${params.row.city}, ${params.row.state}`
              )
            },
            { field: "active_device_kits", headerName: "Device Kits", width: 125 },
            {
              field: "expired_devices",
              headerName: "Expired Devices",
              flex: 1,
              minWidth: 125,
              renderCell: (params) => (
                <span className={params.row.expired_devices > 0 ? 'color-danger' : ''} >{params.row.expired_devices}</span>
              )
            },
            {
              field: "aed_devices",
              headerName: "AEDs",
              flex: 1,
              minWidth: 125,
            },
            {
              field: "expired_aed_devices",
              headerName: "Expired AEDs",
              flex: 1,
              minWidth: 125,
              renderCell: (params) => (
                <span className={params.row.expired_aed_devices > 0 ? 'color-danger' : ''} >{params.row.expired_aed_devices}</span>
              )
            },
            {
              field: "device_message_short",
              headerName: "Message",
              flex: 1,
              minWidth: 200,
              renderCell: (params) => (
                <span className={getComplianceColor(params.row.device_status)} >{params.row.device_message_short}</span>
              )
            }
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: 'device_status',
                  sort: 'desc'
                }
              ]
            }
          }}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
          loading={isLoading}
          autoHeight
          density="compact"
        />
      </Card>
    </>
  )
}

export default ComplianceReport;
